import {
  Chart as ChartJS,
  LinearScale,
  CategoryScale,
  BarElement,
  PointElement,
  Tooltip,
  BarController,
} from 'chart.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Chart, getElementsAtEvent } from 'react-chartjs-2';
import Select from 'react-select';
import debounce from '../../utils/Debounce';
import LoadingChart from '../../components/LoadingChart';
import tooltipIcon from "../../assets/tooltipIcon.svg";
import { Tooltip as ReactTooltip } from 'react-tooltip';
import axiosCMS from '../../libs/axios/axiosCMS';

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, PointElement, Tooltip);

const TopTriMetricsChart = ({ selectedFilters, selectedRole, setSelectedRole, isFocusChart, indexChart, setIndexChart }) => {
  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);
  const [listTooltip, setListTooltip] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [roleOptions, setRoleOptions] = useState([
    { value: '', label: 'All Access' },
    { value: 'PSM', label: 'PSM' },
    { value: 'region', label: 'Region' },
    { value: 'estate', label: 'Estate' },
  ]);

  const handleMetricChange = (selectedOption) => {
    setSelectedRole(selectedOption);
  };

  const data = {
    labels: chartLabels,
    datasets: [
      {
        type: 'bar',
        backgroundColor: 'rgb(113, 174, 235)',
        data: chartData,
        maxBarThickness: 70
      },
    ]
  }

  /**
   * Function to format tooltip data for the chart.
   * @param {*} tooltipItems 
   * @returns 
   */
  const toolTipData = (tooltipItems) => {
    const listToolTip = listTooltip[tooltipItems[0].label] ?? [];
    if (listToolTip.length === 0) {
      return ['No data'];
    }
    const result = listToolTip.map((x, index) => `${index + 1}. ${x.metric_name}: ${x.total}`);
    return result;
  }

  /**
   * Fetch top 3 metrics data based on the selected filters and role.
   * @param {Object} filters - The filters to apply for fetching top metrics.
   * @param {Object} role - The selected role for filtering the data.
   */
  const getTopMetrics = useCallback(debounce(async (filters, role) => {
    setIsLoading(true);
    try {
      const URL = "user-engagement/top-3-metrics-by-access-role";
      const response = await axiosCMS.post(URL, {
        ...filters,
        filter_role: role.value
      });
      const result = response.data;
      setChartData(result.data.map(x => x.total));
      setChartLabels(result.data.map(x => x.role));
      const listTooltip = {};
      result.data.forEach((x) => {
        listTooltip[x.role.toString()] = x.tooltip;
      });
      setListTooltip(listTooltip);
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
    setIsLoading(false);
  }, 1500), []);

  useEffect(() => {
    getTopMetrics(selectedFilters, selectedRole);
  }, [selectedFilters, selectedRole]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: () => '',
          title: (x) => x[0].label,
          afterBody: toolTipData,
        }
      }
    },
  };

  const chartRef = useRef(null)

  const generateFocusListColor = (dataLength, focusIndex) => {
    const colors = [];

    for (let i = 0; i < dataLength; i++) {
      if (i === focusIndex) {
        colors.push('rgb(113, 174, 235)');
      } else {
        colors.push('rgb(218, 224, 222)');
      }
    }
    return colors;
  }

  const generateListColor = useCallback(() => {
    const colors = [];
    for (let i = 0; i < chartLabels.length; i++) {
      colors.push('rgb(113, 174, 235)');
    }
    return colors;
  }, [chartLabels.length]);


  const handleEventChart = (event) => {
    const element = getElementsAtEvent(chartRef.current, event);
    if (element.length > 0) {
      setIndexChart(element[0].index);
    } else {
      setIndexChart(null)
    }
  };

  const handleFocusChart = useCallback(() => {
    const chart = chartRef.current;
    if (indexChart !== null) {
      chart.data.datasets[0].backgroundColor = generateFocusListColor(chartLabels.length, indexChart);
    } else {
      chart.data.datasets[0].backgroundColor = generateListColor();
    }
    chart.update();
  }, [indexChart, chartLabels.length, generateListColor]);

  const firstRender = useRef(true);
  useEffect(() => {
    if(firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (isFocusChart) {
      handleFocusChart();
    }
  }, [indexChart])


  return (
    <div className='ue-section'>
      <div className='ue-filter-container'>
        <h3 className="section-title">
          Top 3 Metrics Queried by Access Role
          <img
            src={tooltipIcon}
            alt="tooltip icon"
            className="tooltip-icon anchor-top-3-metrics-by-access-role"
          />
          <ReactTooltip
            id='tooltip'
            place="top"
            content="Top 3 Metrics that are most frequently queried by users based on access role"
            anchorSelect=".anchor-top-3-metrics-by-access-role"
            style={{ maxWidth: "200px", fontSize: "12px", fontWeight: "400", lineHeight: "1.2", textAlign: "center" }}
          />
        </h3>
        <Select
          id="listRole"
          name="listRole"
          isSearchable={false}
          options={roleOptions}
          value={selectedRole}
          onChange={handleMetricChange}
          placeholder={"-- Select Role Access --"}
          classNamePrefix="select"
        />
      </div>
      <div style={{ position: 'relative', height: '300px' }}>
        {isLoading && <LoadingChart />}
        <Chart type='bar' ref={chartRef} onClick={handleEventChart} data={data} options={chartOptions} />
      </div>
    </div>
  )
}

export default TopTriMetricsChart;